// Button.tsx
import React, { useState, type ButtonHTMLAttributes } from 'react';
import { cn, cx } from '@/utils';
import { Spinner } from '@material-tailwind/react';
import { toast } from 'react-toastify';

import styles from './styles.module.css';

/* ─────────── Botón genérico (versión anterior) ─────────── */

type ButtonProps = {
  text?: string;
  loading?: boolean;
  disabled?: boolean;
  className?: string;
  /** Tailwind para color del texto */
  textClassname?: string;
  /** Tailwind para fondo + hover */
  backgroundColor?: string;
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
} & ButtonHTMLAttributes<HTMLButtonElement>;

export const Button = ({
  text,
  loading = false,
  disabled = false,
  className,
  textClassname = '',
  backgroundColor = 'bg-primary hover:bg-primaryDark',
  iconLeft,
  iconRight,
  ...props
}: ButtonProps) => (
  <button
    className={cx(
      styles.button,
      backgroundColor,
      { [styles.disabled]: disabled || loading },
      iconLeft || iconRight ? 'justify-between' : 'justify-center',
      className
    )}
    disabled={loading || disabled}
    {...props}
  >
    {loading ? (
      <Spinner />
    ) : (
      <>
        {iconLeft}
        {text && <span className={cn(' flex-1 break-words', textClassname)}>{text}</span>}
        {iconRight}
      </>
    )}
  </button>
);

export default Button;

/* ─────────── Botón de descarga ─────────── */

type FileTypes = 'json' | 'csv' | 'xlsx' | string;

type ButtonDownloadProps = {
  downloadLink: string;
  fileType: FileTypes;
  filename?: string;
  /** Token JWT para el header Authorization */
  authToken?: string;
  /** Headers personalizados para la petición */
  customHeaders?: Record<string, string>;
  /** Todas las props del botón salvo onClick y loading */
  buttonProps?: Omit<ButtonProps, 'onClick' | 'loading'>;
  bodyFetch?: Record<string, unknown>;
};

export const ButtonDownload = ({
  downloadLink,
  fileType,
  filename,
  authToken,
  customHeaders,
  buttonProps,
  bodyFetch,
}: ButtonDownloadProps) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    try {
      setIsDownloading(true);

      const response = await fetch(`${process.env.NEXT_PUBLIC_BASE_URL}${downloadLink}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken && { Authorization: `Bearer ${authToken}` }),
          ...(customHeaders && customHeaders),
        },
        body: bodyFetch ? JSON.stringify(bodyFetch) : undefined,
      });

      if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);

      const blob = await response.blob();

      /* Nombre sugerido: Content-Disposition > prop filename > fallback */
      let suggested = filename || `download.${fileType}`;
      const disp = response.headers.get('content-disposition');
      const match = disp?.match(/filename[^;=\n]*=\s*(["']?)([^"';\n]*)\1/i);
      if (match?.[2]) suggested = match[2];

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = suggested;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success('Descarga completada 👍');
    } catch (err) {
      console.error('Error al descargar el archivo', err);
      toast.error('No se pudo completar la descarga. Probá de nuevo.');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Button
      onClick={handleDownload}
      loading={isDownloading}
      disabled={isDownloading || buttonProps?.disabled}
      {...buttonProps}
    />
  );
};
